import {AxiosError} from 'axios';
import {
  BackendErrorData,
  parseApiErrorWithLocalization,
} from '../../utils/custom-errors/parseApiError';
import {apiClient} from '../apiClient';
import {
  IAssignmentDataResponse,
  ICompletedAssignmentTree,
  ICreateByIdResponse,
  IAssignmentGroupNode,
  IGetBulkTagResponse,
  IDeleteAssignmentGroupQuery,
  IUnAssignedUsers,
  IAddAssignedReviewers,
  IGetBulkTagsDetails,
  IEditAssignmentGroupByIdResponse,
  DistributeAssignmentPayload,
  IEditAssignmentByIdResponse,
  IAssignmentSaveRequest,
  ICreateAssignmentResponse,
  IQuickAssign,
  IQuickAssignParams,
  IBulkTagSelectedDocCountParams,
  IBulkTagSelectedDocCountResponse,
  IBulkActionPopupRequest,
  IBulkActionPopupResponse,
  IBulkActionPopupNonBgRequest,
  IBulkActionPopupNonBgResponse,
  IDoActionResponse,
  IDoActionRequest,
  IUnAssignUsersRequest,
  IUnAssignUsersResponse,
  IRemoveDocumentsResponse,
  ICompleteUncompleteAllDocs,
  IDoActionNonBgResponse,
  IDoActionNonBgRequest,
  ICompleteUncompleteAllDocsResponse,
  ICopyAssignmentRequest,
  ICopyAssignmentResponse,
  ICompleteIncompleteDocsPayload,
  IMoveAssignmentGroupRequest,
  IBulkAssignDocListIdRequest,
  IBulkAssignDocListIdResponse,
  IBulkActionLikePopupRequest,
  IBulkActionLikePopupResponse,
  IBulkActionNonbgLikePopupResponse,
  IBulkActionAllDocumentCountRequest,
  IBulkActionAllDocumentCountResponse,
  IQuickCreateRequest,
  IQuickCreateResponse,
  ILikeDocumentCountRequest,
  ILikeDocumentCountResponse,
} from '../types/assignment.type';
import BASE_URL from '../../config/env';
import {
  DocumentAssignmentReqPayload,
  GetReassignUsersListReqPayload,
  ReviewerAssignmentResponse,
} from '../types/reportingApi.type';

const assignmentsEndpoint = (path: string) =>
  `${BASE_URL}/assignments/api/v1/assignment${path}`;

const assignmentsBulkactionsEndpoint = (path: string) =>
  `${BASE_URL}/assignments/api/v1/bulkactions${path}`;

export const getAssignmentList = async (
  queryParams: string
): Promise<IAssignmentDataResponse> => {
  const {data} = await apiClient.get(
    assignmentsEndpoint(`/list/assignmentgrid${queryParams}`)
  );
  return data;
};

export const getCreateById = async (
  queryParams: string
): Promise<ICreateByIdResponse> => {
  const {data} = await apiClient.get(
    assignmentsEndpoint(`/view/createbyid${queryParams}`)
  );
  return data;
};

export const getAssignmentTreeView = async (
  queryParams: number
): Promise<IAssignmentGroupNode[]> => {
  const {data} = await apiClient.get(
    assignmentsEndpoint(`/tree?toggleValue=${queryParams}`)
  );
  return data;
};

export const getCompleteAssignmetTree = async (
  queryParams: number
): Promise<ICompletedAssignmentTree> => {
  const {data} = await apiClient.get(
    assignmentsBulkactionsEndpoint(
      `/view/assignment-complete-tree?showAssignmentDocCount=${queryParams}`
    )
  );
  return data?.data || [];
};

export const getBulkTagList = async (
  actionType: string
): Promise<IGetBulkTagResponse> => {
  const {data} = await apiClient.get(
    assignmentsBulkactionsEndpoint(`/tags?actionType=${actionType}`)
  );
  return data;
};

export const deleteAssignmentGroup = async (
  payload: IDeleteAssignmentGroupQuery
): Promise<string> => {
  const {selectedGroupId, isAssignmentGroup = true} = payload;
  const {data} = await apiClient.delete(
    assignmentsEndpoint(
      `/deletegroup?selectedGroupId=${selectedGroupId}&isAssignmentGroup=${isAssignmentGroup}`
    )
  );
  return data;
};

export const createAssignment = async (payload: any) => {
  const {data} = await apiClient.post(
    assignmentsEndpoint('/create'),
    payload
  );
  return data;
};

export const getDistributedDocs = async (
  assignmentId: number
): Promise<ICreateByIdResponse> => {
  const {data} = await apiClient.get(
    assignmentsEndpoint(`/bind/distributed-docs?assignmentId=${assignmentId}`)
  );
  return data;
};

export const getEditAssignmentGroupById = async (
  queryParams: string
): Promise<IEditAssignmentGroupByIdResponse> => {
  const {data} = await apiClient.get(
    assignmentsEndpoint(`/view/editassignmentgroupbyid${queryParams}`)
  );
  return data;
};

export const getUnAssignedUsers = async (
  assignmentId?: number
): Promise<IUnAssignedUsers> => {
  const queryParams = new URLSearchParams();

  if (assignmentId) {
    queryParams.append('assignmentId', String(assignmentId));
  }
  const queryString = queryParams.toString();
  const {data} = await apiClient.get(
    assignmentsEndpoint(
      `/bind/not-assigned-users${queryString ? `?${queryString}` : ''}`
    )
  );
  return data;
};

export const addAssignedReviewers = async (payload: IAddAssignedReviewers) => {
  const {data} = await apiClient.post(
    assignmentsEndpoint('/reviewers/add'),
    payload
  );
  return data;
};

export const getBulkTagsDetails = async (): Promise<IGetBulkTagsDetails> => {
  const {data} = await apiClient.get(
    assignmentsBulkactionsEndpoint(`/bulktags`)
  );
  return data;
};

export const editAssignmentGroup = async (payload: any) => {
  try {
    const {data} = await apiClient.post(
      assignmentsEndpoint('/groups/edit'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'Assignments'
    );
  }
};

export const distributeAssignment = async (
  payload: DistributeAssignmentPayload
) => {
  const {data} = await apiClient.post(
    assignmentsEndpoint('/documents/distribute'),
    payload
  );
  return data;
};

export const completeUncompleteAllDocs = async (
  payload: ICompleteUncompleteAllDocs
): Promise<ICompleteUncompleteAllDocsResponse> => {
  const {assignmentId, completealldocuments = false} = payload;
  const {data} = await apiClient.get(
    assignmentsEndpoint(
      `/documents/${completealldocuments ? 'completealldocuments' : 'incompletealldocuments'}?assignmentId=${assignmentId}`
    )
  );
  return data;
};

export const updateAssignment = async (
  payload: IAssignmentSaveRequest
): Promise<ICreateAssignmentResponse> => {
  const {data} = await apiClient.post(
    assignmentsEndpoint('/assignments/edit'),
    payload
  );
  return data;
};

export const editAssignmentById = async (
  queryParams: string
): Promise<IEditAssignmentByIdResponse> => {
  const {data} = await apiClient.get(
    assignmentsEndpoint(`/view/editassignmentbyid${queryParams}`)
  );
  return data;
};

export const getQuickAssign = async (
  params: IQuickAssignParams
): Promise<IQuickAssign> => {
  const queryParams = new URLSearchParams();
  const {elementId, masterDocListId, actionType, subActionType, sourceType} =
    params;

  if (elementId) {
    queryParams.append('ElementId', String(elementId));
  }
  if (masterDocListId) {
    queryParams.append('MasterDocListId', String(masterDocListId));
  }
  if (actionType) {
    queryParams.append('ActionType', String(actionType));
  }
  if (subActionType) {
    queryParams.append('SubActionType', String(subActionType));
  }
  if (sourceType) {
    queryParams.append('SourceType', String(sourceType));
  }

  const queryString = queryParams.toString();
  const {data} = await apiClient.get(
    assignmentsBulkactionsEndpoint(
      `/quickassign${queryString ? `?${queryString}` : ''}`
    )
  );
  return data;
};

export const getBulkTagSelectedDocCount = async (
  params: IBulkTagSelectedDocCountParams
): Promise<IBulkTagSelectedDocCountResponse> => {
  const queryParams = new URLSearchParams();
  const {masterDocListId} = params;
  if (masterDocListId !== undefined && masterDocListId !== null) {
    queryParams.append('masterDocListId', String(masterDocListId));
  }
  const queryString = queryParams.toString();
  const {data} = await apiClient.get(
    assignmentsBulkactionsEndpoint(
      `/background/status${queryString ? `?${queryString}` : ''}`
    )
  );
  return data;
};

export const callBulkActionsPopup = async (
  payload: IBulkActionPopupRequest
): Promise<IBulkActionPopupResponse> => {
  const {data} = await apiClient.post(
    assignmentsBulkactionsEndpoint('/bulkpopup'),
    payload
  );
  return data;
};

export const callBulkActionsPopupNonBg = async (
  payload: IBulkActionPopupNonBgRequest
): Promise<IBulkActionPopupNonBgResponse> => {
  const {data} = await apiClient.post(
    assignmentsBulkactionsEndpoint('/bulkpopup-nonbg'),
    payload
  );
  return data;
};

export const doAction = async (
  payload: IDoActionRequest
): Promise<IDoActionResponse> => {
  const {data} = await apiClient.post(
    assignmentsBulkactionsEndpoint('/actions/execute'),
    payload
  );
  return data;
};

export const unAssignUser = async (
  payload: IUnAssignUsersRequest
): Promise<IUnAssignUsersResponse> => {
  const {data} = await apiClient.post(
    assignmentsEndpoint('/documents/unassignuser'),
    payload
  );
  return data;
};

export const doActionNonBg = async (
  payload: IDoActionNonBgRequest
): Promise<IDoActionNonBgResponse> => {
  const {data} = await apiClient.post(
    assignmentsBulkactionsEndpoint('/actions/execute/nonbg'),
    payload
  );
  return data;
};

export const removeDocuments = async (
  queryParams: string
): Promise<IRemoveDocumentsResponse> => {
  const {data} = await apiClient.delete(
    assignmentsEndpoint(`/documents/remove?${queryParams}`)
  );
  return data;
};

export const copyAssignment = async (
  payload: ICopyAssignmentRequest
): Promise<ICopyAssignmentResponse> => {
  const {data} = await apiClient.post(
    assignmentsEndpoint('/copy'),
    payload
  );
  return data;
};

export const completeDocs = async (
  payload: ICompleteIncompleteDocsPayload
): Promise<ICompleteUncompleteAllDocsResponse> => {
  const {assignmentId, reviewerId} = payload;
  const {data} = await apiClient.post(
    assignmentsEndpoint(
      `/documents/completedocuments?assignmentId=${assignmentId}&reviewerId=${reviewerId}`
    )
  );
  return data;
};

export const incompleteDocs = async (
  payload: ICompleteIncompleteDocsPayload
): Promise<ICompleteUncompleteAllDocsResponse> => {
  const {data} = await apiClient.post(
    assignmentsEndpoint('/documents/incompletedocuments'),
    payload
  );
  return data;
};

export const moveAssignmentGroup = async (
  payload: IMoveAssignmentGroupRequest
): Promise<any> => {
  const {data} = await apiClient.post(
    assignmentsEndpoint('/groups/move'),
    payload
  );
  return data;
};

export const getBulkAssignDocListId = async (
  payload: IBulkAssignDocListIdRequest
): Promise<IBulkAssignDocListIdResponse> => {
  const {data} = await apiClient.post(
    assignmentsBulkactionsEndpoint('/doclist/id'),
    payload
  );
  return data;
};

export const callBulkActionsLikePopup = async (
  payload: IBulkActionLikePopupRequest
): Promise<IBulkActionLikePopupResponse> => {
  const {data} = await apiClient.post(
    assignmentsBulkactionsEndpoint('/likepopup'),
    payload
  );
  return data;
};

export const callBulkActionsLikePopupNonbg = async (
  payload: IBulkActionLikePopupRequest
): Promise<IBulkActionNonbgLikePopupResponse> => {
  const {data} = await apiClient.post(
    assignmentsBulkactionsEndpoint('/likepopup-nonbg'),
    payload
  );
  return data;
};

export const getBulkActionAllDocumentCount = async (
  params: IBulkActionAllDocumentCountRequest
): Promise<IBulkActionAllDocumentCountResponse> => {
  const queryParams = new URLSearchParams();
  const {masterDocListId} = params;
  if (masterDocListId !== undefined && masterDocListId !== null) {
    queryParams.append('masterDocListId', String(masterDocListId));
  }
  const queryString = queryParams.toString();
  const path = queryString ? `/count/all?${queryString}` : '/count/all';
  const {data} = await apiClient.get(
    assignmentsBulkactionsEndpoint(path)
  );
  return data;
};

export const quickCreate = async (
  payload: IQuickCreateRequest
): Promise<IQuickCreateResponse> => {
  const {data} = await apiClient.post(
    assignmentsEndpoint('/quick-create'),
    payload
  );
  return data;
};

export const getReassignUserList = async (
  payload: GetReassignUsersListReqPayload
): Promise<ReviewerAssignmentResponse> => {
  const {data} = await apiClient.post(
    assignmentsEndpoint('/reassign/users'),
    payload
  );
  return data;
};

export const documentReassign = async (
  payload: DocumentAssignmentReqPayload
): Promise<any> => {
  const {data} = await apiClient.post(
    assignmentsEndpoint('/documents/reassign'),
    payload
  );
  return data;
};

export const getLikeDocumentCountNonBG = async (
  payload: ILikeDocumentCountRequest
): Promise<ILikeDocumentCountResponse> => {
  const {data} = await apiClient.post(
    assignmentsBulkactionsEndpoint('/count/like-documents/nonbg'),
    payload
  );
  return data;
};
